/**
 * i18n_ — locale-aware formatting helpers.
 *
 * Frontend counterpart to `apps/api/app/Services/Locale/LocaleFormatter.php`.
 * All functions accept an explicit `locale` argument (BCP-47 tag) so the
 * caller can drive it from the auth store or i18next state.
 *
 * Falls back to `pl-PL` when the locale is empty or invalid — this matches
 * the backend default and keeps existing screens working during the
 * roll-out.
 */

const DEFAULT_LOCALE = 'pl-PL';

/**
 * Map a short i18next code (pl / en / de / uk / cs) to a full BCP-47 tag
 * that Intl.* accepts without warnings.
 */
export function toBcp47(locale: string | undefined | null): string {
  if (!locale) return DEFAULT_LOCALE;
  const lower = locale.toLowerCase();
  if (lower.includes('-')) return lower;
  switch (lower) {
    case 'pl':
      return 'pl-PL';
    case 'en':
      return 'en-US';
    case 'de':
      return 'de-DE';
    case 'uk':
      return 'uk-UA';
    case 'cs':
      return 'cs-CZ';
    default:
      return DEFAULT_LOCALE;
  }
}

export function formatNumber(
  value: number,
  locale: string | undefined = DEFAULT_LOCALE,
  options: Intl.NumberFormatOptions = {},
): string {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat(toBcp47(locale), options).format(value);
}

export function formatCurrency(
  value: number,
  currency = 'PLN',
  locale: string | undefined = DEFAULT_LOCALE,
): string {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat(toBcp47(locale), {
    style: 'currency',
    currency: currency || 'PLN',
  }).format(value);
}

export function formatPercent(
  value: number,
  locale: string | undefined = DEFAULT_LOCALE,
  fractionDigits = 0,
): string {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat(toBcp47(locale), {
    style: 'percent',
    maximumFractionDigits: fractionDigits,
  }).format(value / 100);
}

export type DateStyle = 'short' | 'medium' | 'long' | 'full';

export function formatDate(
  date: Date | string | number,
  locale: string | undefined = DEFAULT_LOCALE,
  style: DateStyle = 'medium',
): string {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(toBcp47(locale), { dateStyle: style }).format(d);
}

export function formatDateTime(
  date: Date | string | number,
  locale: string | undefined = DEFAULT_LOCALE,
  dateStyle: DateStyle = 'short',
  timeStyle: DateStyle = 'short',
): string {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(toBcp47(locale), { dateStyle, timeStyle }).format(d);
}

/**
 * Formats a point in time relative to "now" — e.g. "3 hours ago", "in 2 days".
 * Useful for activity feeds, last-sync columns and notifications.
 */
export function formatRelativeTime(
  date: Date | string | number,
  locale: string | undefined = DEFAULT_LOCALE,
): string {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '—';
  const rtf = new Intl.RelativeTimeFormat(toBcp47(locale), { numeric: 'auto' });
  const deltaSec = Math.round((d.getTime() - Date.now()) / 1000);
  const absSec = Math.abs(deltaSec);

  if (absSec < 60) return rtf.format(deltaSec, 'second');
  if (absSec < 3600) return rtf.format(Math.round(deltaSec / 60), 'minute');
  if (absSec < 86400) return rtf.format(Math.round(deltaSec / 3600), 'hour');
  if (absSec < 2592000) return rtf.format(Math.round(deltaSec / 86400), 'day');
  if (absSec < 31536000) return rtf.format(Math.round(deltaSec / 2592000), 'month');
  return rtf.format(Math.round(deltaSec / 31536000), 'year');
}
